import { useState, useCallback } from "react";
import { OutlookCalendarService } from "../../services/OutlookCalendarService";
import { RoomService } from "../../services/RoomService";
import { Room, AppointmentDetails, BookingConfirmation } from "../../types";
import { AppError, ErrorCode } from "../../errors/AppError";
import { ERROR_MESSAGES } from "../../constants/messages";

interface UseRoomBookingResult {
  bookingInProgress: string | null;
  bookingError: AppError | null;
  confirmation: BookingConfirmation | null;
  bookRoom: (room: Room, appointmentDetails: AppointmentDetails) => Promise<void>;
  bookRoomViaDeskare: (room: Room, appointmentDetails: AppointmentDetails) => Promise<void>;
  dismissError: () => void;
  dismissConfirmation: () => void;
}

export function useRoomBooking(): UseRoomBookingResult {
  const [bookingInProgress, setBookingInProgress] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<AppError | null>(null);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);

  const bookRoom = useCallback(async (
    room: Room,
    appointmentDetails: AppointmentDetails
  ): Promise<void> => {
    setBookingInProgress(room.id);
    setBookingError(null);

    try {
      if (!room.email) {
        throw new AppError(
          ERROR_MESSAGES.ROOM_NO_EMAIL,
          ErrorCode.ROOM_NO_EMAIL
        );
      }

      await OutlookCalendarService.addRoomAsAttendee(room.email, room.name);
      // updateLocation and storeBookingId are independent — run in parallel
      await Promise.all([
        OutlookCalendarService.updateLocation(room.name),
        OutlookCalendarService.storeBookingId(room.id, room.id),
      ]);

      setConfirmation({
        roomName: room.name,
        roomImage: room.imageUrl,
        startTime: appointmentDetails.start,
        endTime: appointmentDetails.end,
        attendeeCount: OutlookCalendarService.getAttendeeCount(appointmentDetails),
      });
    } catch (err) {
      setBookingError(AppError.from(err));
    } finally {
      setBookingInProgress(null);
    }
  }, []);

  const bookRoomViaDeskare = useCallback(async (
    room: Room,
    appointmentDetails: AppointmentDetails
  ): Promise<void> => {
    setBookingInProgress(room.id);
    setBookingError(null);

    try {
      const { bookingId } = await RoomService.createDeskareBooking(
        room.id,
        appointmentDetails.start,
        appointmentDetails.end,
        {
          name: appointmentDetails.subject || undefined,
        }
      );

      await Promise.all([
        OutlookCalendarService.updateLocation(room.name),
        OutlookCalendarService.storeBookingId(bookingId, room.id),
      ]);

      setConfirmation({
        roomName: room.name,
        roomImage: room.imageUrl,
        startTime: appointmentDetails.start,
        endTime: appointmentDetails.end,
        attendeeCount: OutlookCalendarService.getAttendeeCount(appointmentDetails),
      });
    } catch (err) {
      setBookingError(AppError.from(err));
    } finally {
      setBookingInProgress(null);
    }
  }, []);

  const dismissError = useCallback(() => {
    setBookingError(null);
  }, []);

  const dismissConfirmation = useCallback(() => {
    setConfirmation(null);
  }, []);

  return {
    bookingInProgress,
    bookingError,
    confirmation,
    bookRoom,
    bookRoomViaDeskare,
    dismissError,
    dismissConfirmation,
  };
}
